import { createClient, Client, Stream, createStream, StreamSpec, getDevices, MediaDeviceInfo } from 'agora-rtc-sdk'
import { AGORA_APP_ID } from 'components/features/videoCalls/constants'
import { fromEvent, merge, Observable, of, from, EMPTY } from 'rxjs'
import { AgoraClientEventType, StreamId, AgoraStreamId, AgoraStreamEventType } from 'components/features/videoCalls/types/agoraTypes'
import { AgoraStopScreenShareAction, AgoraClientEventAction, AgoraStreamEventAction } from 'components/features/videoCalls/types/actions'
import { agoraInstances } from 'components/features/videoCalls/agoraInstances'
import { map, tap, mergeMap, catchError, filter } from 'rxjs/operators'
import { mergeMapAsync } from 'rxOperators/mergeMapAsync'
import { mergeLast } from 'rxOperators/mergeLast'
import { ENV } from 'constants/app'

export const createAgoraClient = async (token: string, channel: string, uid: string) => {
  const client = createClient({
    mode: 'rtc',
    codec: 'vp8',
  })

  await new Promise<void>((res, rej) => client.init(AGORA_APP_ID, res, rej))

  const localStreamId = +(await new Promise<AgoraStreamId>((res, rej) => client.join(token, channel, uid, undefined, res, rej)))
  return {
    client,
    localStreamId,
  }
}

export const agoraClientLeave = async (client: Client) => {
  await new Promise<void>((res, rej) => client.leave(res, rej))
}

export const createAgoraClientLifeCycleStream = (token: string, channel: string, uid: string, until$: Observable<any>) => {
  return new Observable((subject) => subject.next(null)).pipe(
    mergeMapAsync(async () => {
      return await createAgoraClient(token, channel, uid)
    }),
    tap(({ client }) => client.enableAudioVolumeIndicator()),

    map((event) => ({
      ...event,
      clientId: agoraInstances.addClient(event.client),
    })),

    mergeLast(until$, (last$) => last$.pipe(
      mergeMapAsync(async ({ client, clientId }) => {
        await agoraClientLeave(client)
        agoraInstances.clients.delete(clientId)
      }),

      mergeMap(() => EMPTY)
    )),
  )
}


export const publishStream = (client: Client, stream: Stream) => new Promise<Stream>((resolve, reject) => {
  client.publish(stream, reject)
  resolve(stream)
})

export const unpublishStream = (client: Client, stream: Stream) => new Promise<Stream>((resolve, reject) => {
  const handler = () => {
    client.off('stream-unpublished', handler)
    resolve(stream)
  }

  client.on('stream-unpublished', handler)
  client.unpublish(stream, reject)

})

export type CreateStreamProps =
  & Omit<StreamSpec, 'streamID'>
  & {

  }

export const createAgoraStream = async (streamId: StreamId, streamProps: CreateStreamProps) => {
  const stream = createStream({
    streamID: streamId,
    ...streamProps,
  })

  await new Promise<void>((res, rej) => stream.init(res, rej))
  return stream
}

const createStreamLifeCycleStream = (streamId: StreamId, streamProps: CreateStreamProps, until$: Observable<any>) => {
  return new Observable((subject) => subject.next(null)).pipe(
    mergeMap(() => from(createAgoraStream(streamId, streamProps))),
    tap((stream) => agoraInstances.streams.set(streamId, stream)),
    mergeLast(until$, (last$) => last$.pipe(
      tap((stream) => stream.close()),
      tap(() => agoraInstances.streams.delete(streamId)),
      mergeMap(() => EMPTY)
    )),
  )
}


export const createPublishedStreamLifeCycleStream = (client: Client, streamId: StreamId, streamProps: CreateStreamProps, until$: Observable<any>) => {

  return createStreamLifeCycleStream(streamId, streamProps, until$).pipe(
    mergeMap((stream) => from(publishStream(client, stream)).pipe(
      catchError(() => {
        return of(null)
      }),
    )),
    mergeLast(until$, (last$) => last$.pipe(
      filter((stream): stream is NonNullable<typeof stream> => !!stream),
      mergeMap((stream) => from(unpublishStream(client, stream))),
      mergeMap(() => EMPTY)
    )),

  )
}

const streamToEventEmitter = (stream: Stream) => {
  return {
    ...stream,
    off: () => { /* do nothing */ },
  }
}

export const fromScreenStreamEvents = (stream: Stream) => merge(
  fromEvent(streamToEventEmitter(stream), 'stopScreenSharing').pipe(map(() => new AgoraStopScreenShareAction())),
)


const allAgoraClientEvents = [
  'first-audio-frame-decode',
  'first-video-frame-decode',
  'stream-published',
  'stream-unpublished',
  'stream-added',
  'stream-removed',
  'stream-subscribed',
  'peer-online',
  'peer-leave',
  'mute-audio',
  'unmute-audio',
  'mute-video',
  'unmute-video',
  'crypt-error',
  'client-banned',
  'active-speaker',
  // 'volume-indicator',
  'liveStreamingStarted',
  'liveStreamingFailed',
  'liveStreamingStopped',
  'liveTranscodingUpdated',
  'streamInjectedStatus',
  'onTokenPrivilegeWillExpire',
  'onTokenPrivilegeDidExpire',
  'error',
  'network-type-changed',
  'recording-device-changed',
  'playout-device-changed',
  'camera-changed',
  'stream-type-changed',
  'connection-state-change',
  'stream-reconnect-start',
  'stream-reconnect-end',
  'client-role-changed',
  'reconnect',
  'rejoin',
  'connected',
  // 'network-quality',
  'enable-local-video',
  'disable-local-video',
  'channel-media-relay-event',
  'channel-media-relay-state',
]

if (ENV !== 'local') {
  // it's really annoying event
  allAgoraClientEvents.push('volume-indicator')
}

export const fromClientEvents = (client: Client) => {
  return merge(
    ...allAgoraClientEvents.map((eventName) => fromEvent<AgoraClientEventType>(client, eventName).pipe(
      map((event) => new AgoraClientEventAction(event))
    ))
  )
}

const allAgoraStreamEvents = [
  'accessAllowed',
  'accessDenied',
  'stopScreenSharing',
  'videoTrackEnded',
  'audioTrackEnded',
  'audioMixingPlayed',
  'audioMixingFinished',
  'player-status-change',
]

export const fromStreamEvents = (stream: Stream) => {
  return merge(
    ...allAgoraStreamEvents.map((eventName) => fromEvent<AgoraStreamEventType>(streamToEventEmitter(stream), eventName).pipe(
      map((event) => new AgoraStreamEventAction(event))
    ))
  )
}

export const getAgoraDevices = () => new Promise<MediaDeviceInfo[]>(getDevices)

export const filterInputDevices = async (kind: MediaDeviceKind) => {
  const devices = await getAgoraDevices()
  return devices.filter((device) => device.kind === kind)
}
